import {
  ApolloClient,
  DataProxy,
  FetchResult,
  useMutation,
  useQuery,
  gql,
} from '@apollo/client';
import { useCallback } from 'react';

import type { DepartmentDeviceQl } from '../__generated__/graphql';
import { safeRead } from '../util';

// GraphQL Queries
export const GET_DEPARTMENT_DEVICES = gql`
  query GetDepartmentDevices($departmentId: String!) {
    departmentDevices(departmentId: $departmentId) {
      id
      name
      mdn
      sipAddress
      type
      enabledDuringBusinessHours
      enabledAfterBusinessHours
      createdOn
      lastUpdated
    }
  }
`;

// GraphQL Mutations
export const CREATE_DEPARTMENT_DEVICE = gql`
  mutation CreateDepartmentDevice($input: CreateDepartmentDeviceInput!) {
    createDepartmentDevice(input: $input) {
      device {
        id
        name
        mdn
        sipAddress
        type
        enabledDuringBusinessHours
        enabledAfterBusinessHours
        createdOn
        lastUpdated
      }
    }
  }
`;

export const UPDATE_DEPARTMENT_DEVICE = gql`
  mutation UpdateDepartmentDevice($input: UpdateDepartmentDeviceInput!) {
    updateDepartmentDevice(input: $input) {
      device {
        id
        name
        mdn
        sipAddress
        type
        enabledDuringBusinessHours
        enabledAfterBusinessHours
        createdOn
        lastUpdated
      }
    }
  }
`;

export const DELETE_DEPARTMENT_DEVICE = gql`
  mutation DeleteDepartmentDevice($id: String!) {
    deleteDepartmentDevice(id: $id) {
      success
      message
    }
  }
`;

// React Hooks for Queries
export const useGetDepartmentDevices = (departmentId: string) => {
  const { data, loading, error, refetch } = useQuery(GET_DEPARTMENT_DEVICES, {
    variables: { departmentId },
    fetchPolicy: 'cache-and-network',
    skip: !departmentId,
  });

  const departmentDevices = data?.departmentDevices || [];
  return { departmentDevices, loading, error, refetch };
};

// React Hooks for Mutations
export const useCreateDepartmentDevice = () => {
  const [_createDepartmentDevice] = useMutation(CREATE_DEPARTMENT_DEVICE);

  return useCallback(
    ({
      client,
      departmentId,
      mdn,
      sipAddress,
      deviceType,
      enabledDuringBusinessHours = true,
      enabledAfterBusinessHours = false,
    }: {
      client: ApolloClient<any>;
      departmentId: string;
      mdn: string;
      sipAddress?: string | null;
      deviceType: 'DESK' | 'MOBILE';
      enabledDuringBusinessHours?: boolean;
      enabledAfterBusinessHours?: boolean;
    }): Promise<FetchResult<any>> => {
      const result = safeRead(client, {
        query: GET_DEPARTMENT_DEVICES,
        variables: { departmentId },
      });

      const departmentDevices = result?.departmentDevices || [];
      const deviceName = deviceType === 'DESK' ? 'Desk Phone' : 'Mobile Phone';

      return client.mutate({
        mutation: CREATE_DEPARTMENT_DEVICE,
        variables: {
          input: {
            departmentId,
            name: deviceName,
            mdn,
            sipAddress,
            type: deviceType,
            enabledDuringBusinessHours,
            enabledAfterBusinessHours,
          },
        },
        optimisticResponse: {
          createDepartmentDevice: {
            __typename: 'CreateDepartmentDevice',
            device: {
              __typename: 'DepartmentDeviceQL',
              id: 'optimistic-response-id',
              name: deviceName,
              mdn,
              sipAddress,
              type: deviceType,
              enabledDuringBusinessHours,
              enabledAfterBusinessHours,
            },
          },
        },
        update: (cache: DataProxy, response: FetchResult<any>) => {
          if (!response.data?.createDepartmentDevice?.device) {
            return;
          }

          const deviceResult = response.data.createDepartmentDevice.device;

          cache.writeQuery({
            query: GET_DEPARTMENT_DEVICES,
            variables: { departmentId },
            data: {
              departmentDevices: [...departmentDevices, deviceResult],
            },
          });
        },
      });
    },
    [],
  );
};

export const useUpdateDepartmentDevice = () => {
  const [_updateDepartmentDevice] = useMutation(UPDATE_DEPARTMENT_DEVICE);

  return useCallback(
    ({
      client,
      device,
      mdn,
      sipAddress,
      enabledDuringBusinessHours,
      enabledAfterBusinessHours,
    }: {
      client: ApolloClient<any>;
      device: DepartmentDeviceQl;
      mdn?: string;
      sipAddress?: string | null;
      enabledDuringBusinessHours?: boolean;
      enabledAfterBusinessHours?: boolean;
    }): Promise<FetchResult<any>> => {
      return client.mutate({
        mutation: UPDATE_DEPARTMENT_DEVICE,
        variables: {
          input: {
            id: device.id,
            mdn,
            sipAddress,
            enabledDuringBusinessHours,
            enabledAfterBusinessHours,
          },
        },
        optimisticResponse: {
          updateDepartmentDevice: {
            __typename: 'UpdateDepartmentDevice',
            device: {
              ...device,
              mdn: mdn ?? device.mdn,
              sipAddress: sipAddress ?? device.sipAddress,
              enabledDuringBusinessHours:
                enabledDuringBusinessHours ?? device.enabledDuringBusinessHours,
              enabledAfterBusinessHours:
                enabledAfterBusinessHours ?? device.enabledAfterBusinessHours,
            },
          },
        },
      });
    },
    [],
  );
};

export const useDeleteDepartmentDevice = () => {
  const [_deleteDepartmentDevice] = useMutation(DELETE_DEPARTMENT_DEVICE);

  return useCallback(
    ({
      client,
      deviceId,
      departmentId,
    }: {
      client: ApolloClient<any>;
      deviceId: string;
      departmentId: string;
    }): Promise<FetchResult<any>> => {
      const { departmentDevices } = safeRead(client, {
        query: GET_DEPARTMENT_DEVICES,
        variables: { departmentId },
      });

      return client.mutate({
        mutation: DELETE_DEPARTMENT_DEVICE,
        variables: {
          id: deviceId,
        },
        optimisticResponse: {
          deleteDepartmentDevice: {
            __typename: 'DeleteDepartmentDevice',
            success: true,
            message: 'Device deleted successfully',
          },
        },
        update: (cache: DataProxy, response: FetchResult<any>) => {
          if (!response.data?.deleteDepartmentDevice?.success) {
            return;
          }

          cache.writeQuery({
            query: GET_DEPARTMENT_DEVICES,
            variables: { departmentId },
            data: {
              departmentDevices: departmentDevices.filter(
                (device: DepartmentDeviceQl) => device.id !== deviceId,
              ),
            },
          });
        },
      });
    },
    [],
  );
};
